import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { updateLocationAgainstSegment } from 'planning-portal-library/src/lib/core/domain/requests/jobs/update-location-against-segment.request';
import { BulkUpdateLocationRequest } from 'planning-portal-library/src/lib/core/domain/requests/locations/bulk-update-location.request';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  reduce,
  share,
} from 'rxjs';
import {
  AddEditLocationRequest,
  EditLocationCalculateETARequest,
  GetLocationIdsRequest,
  GetLocationLookupRequest,
  GetLocationsRequest,
  GetPlanJobsLocationsRequest,
  Location,
  LocationLookup,
  LocationUpdateNotification,
  PaginationRequest,
  PlanJobsLocationsListResponse,
  RemoveLocationRequest,
  RemoveOrUnassignedLocationsRequest,
  UpdateLocationsFromMlsRequest,
} from '../../../../core/domain';
import { LocationsRepository } from '../../../../core/repositories';
import { BaseGridResponse } from '../../../shared/base-grid.response';
import { BaseRequest } from '../../../shared/base.request';
import { BaseResponse } from '../../../shared/base.response';
import { LocationUpdateNotificationWebRepositoryMapper } from './entities/location-update-notification-web-repository-mapper.ts';
import { LocationWebEntity } from './entities/location-web-entity';
import { LocationWebRepositoryMapper } from './entities/location-web-repository-mapper';
import { LocationsApiUrls } from './location-api-urls';

import { LocationTag } from 'planning-portal-library/src/lib/core/domain/models/location-tag';
import { UpdateLocationDetailRequest } from 'planning-portal-library/src/lib/core/domain/requests/locations/update-location-details-request';
import { injectEnvironment } from 'planning-portal-library/src/lib/core/environments/environment.token';
import { LocationLookupWebEntity } from '../../lookups/lookups-web-repository/location-lookup-web-entity';
import { LocationLookupWebRepositoryMapper } from '../../lookups/lookups-web-repository/location-lookup-web-repository-mapper';
import { LocationTagMlsWebEntity } from './entities/location-tag-mls-web-entity';
import { LocationTagMlsWebRepositoryMapper } from './entities/location-tag-mls-web-repository-mapper';
import { LocationUpdateNotificationWebEntity } from './entities/location-update-notification-web-entity.ts';

@Injectable({ providedIn: 'root' })
export class LocationWebRepository extends LocationsRepository {
  private readonly locationMapper = new LocationWebRepositoryMapper();
  private readonly locationTagMapper = new LocationTagMlsWebRepositoryMapper();
  private readonly locationNotificationMapper =
    new LocationUpdateNotificationWebRepositoryMapper();
  private readonly environment = injectEnvironment();

  private readonly locationLookupWebRepositoryMapper =
    new LocationLookupWebRepositoryMapper();
  constructor(private readonly http: HttpClient) {
    super();
  }

  getLocations(
    request: BaseRequest<GetLocationsRequest>
  ): Observable<BaseGridResponse<Location>> {
    const httpMap = this.http
      .post<BaseResponse<BaseGridResponse<LocationWebEntity>>>(
        `${this.environment.url}${LocationsApiUrls.GetLocations}`,
        request.request
      )
      .pipe(share());

    const dataResultMap = httpMap
      .pipe(
        mergeMap((response) => response.result!.data),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.locationMapper.mapFrom))
      .pipe(
        reduce((items: Location[], item: Location) => [...items, item], [])
      );

    const totalRecordsResultMap = httpMap.pipe(
      map((response) => response.result!.totalRecords)
    );

    return forkJoin([dataResultMap, totalRecordsResultMap]).pipe(
      map((input) => {
        const item: BaseGridResponse<Location> = {
          data: input[0],
          totalRecords: +input[1],
        };
        return item;
      })
    );
  }

  getLocationUpdatesNotification(
    request: PaginationRequest
  ): Observable<LocationUpdateNotification[]> {
    return this.http
      .post<BaseResponse<LocationUpdateNotificationWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetLocationsUpdate}`,
        request
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.locationNotificationMapper.mapFrom))
      .pipe(
        reduce(
          (
            items: LocationUpdateNotification[],
            item: LocationUpdateNotification
          ) => [...items, item],
          []
        )
      );
  }

  updateLocationFromMls(
    request: UpdateLocationsFromMlsRequest
  ): Observable<Response> {
    return this.http
      .post<Response>(
        `${this.environment.url}${LocationsApiUrls.UpdateLocationsFromMls}`,
        request
      )
      .pipe(
        map((data: Response) => {
          return data;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  bulkUpdateLocation(request: BulkUpdateLocationRequest): Observable<boolean> {
    return this.http
      .patch<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.UpdateLocations}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  editLocationCalculateJobETA(
    request: EditLocationCalculateETARequest
  ): Observable<boolean> {
    return this.http
      .patch<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.UpdateLocationsAndCalculateJobETA}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getTagsMLS(): Observable<LocationTag[] | undefined> {
    return this.http
      .get<BaseResponse<LocationTagMlsWebEntity[]>>(
        `${this.environment.masterLocationUrl}${LocationsApiUrls.GetMlsTags}`
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.locationTagMapper.mapFrom))
      .pipe(
        reduce(
          (items: LocationTag[], item: LocationTag) => [...items, item],
          []
        )
      );
  }

  updatePlanLocationAgainstSegment(
    request: updateLocationAgainstSegment
  ): Observable<boolean> {
    return this.http
      .patch<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.UpdateLocationAgainstSegmentAndCalculateJobETA}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  editLocation(request: AddEditLocationRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.BindLocationWithJob}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  deleteLocationFromJob(request: string): Observable<boolean> {
    return this.http
      .delete<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.DeleteLocationFromJob}`,
        {
          params: {
            id: request,
          },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  removeLocation(request: RemoveLocationRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.DeleteLocation}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  unassignLocation(request: RemoveLocationRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.UnassignLocation}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getLocationById(request: string): Observable<Location> {
    return this.http
      .get<BaseResponse<LocationWebEntity>>(
        `${this.environment.url}${LocationsApiUrls.GetLocationById}`,
        {
          params: {
            id: request,
          },
        }
      )
      .pipe(
        map((response: BaseResponse<LocationWebEntity>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.locationMapper.mapFrom));
  }

  getUnAssignedLocations(request: string): Observable<Location[]> {
    return this.http
      .get<BaseResponse<LocationWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetUnAssignedLocations}`,
        {
          params: {
            planId: request,
          },
        }
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.locationMapper.mapFrom))
      .pipe(
        reduce((items: Location[], item: Location) => [...items, item], [])
      );
  }

  GetPlanAndJobsLocations(
    request: GetPlanJobsLocationsRequest
  ): Observable<PlanJobsLocationsListResponse> {
    return this.http
      .post<BaseResponse<PlanJobsLocationsListResponse>>(
        `${this.environment.url}${LocationsApiUrls.GetPlanAndJobsLocations}`,
        request
      )
      .pipe(
        map(
          (response: BaseResponse<PlanJobsLocationsListResponse>) =>
            response.result!
        ),
        catchError((err) => {
          throw err;
        })
      );
  }

  updatePlanLocation(request: AddEditLocationRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.UpdatePlanLocation}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  updateLocationDetail(
    request: UpdateLocationDetailRequest[]
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.UpdateLocationDetails}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getLocationsByPlanId(request: string): Observable<Location[]> {
    return this.http
      .get<BaseResponse<LocationWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetLocationsByPlanId}`,
        {
          params: {
            planId: request,
          },
        }
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.locationMapper.mapFrom))
      .pipe(
        reduce((items: Location[], item: Location) => [...items, item], [])
      );
  }
  GetLocationBySegmentId(
    request: GetLocationIdsRequest
  ): Observable<Location[]> {
    return this.http
      .post<BaseResponse<LocationWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetLocationBySegmentId}`,
        request
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.locationMapper.mapFrom))
      .pipe(
        reduce((items: Location[], item: Location) => [...items, item], [])
      );
  }
  deleteAllUnAssignedLocation(request: string): Observable<boolean> {
    return this.http
      .delete<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.DeleteAllUnAssignedLocation}`,
        {
          params: {
            planId: request,
          },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  removeOrUnassignedLocations(
    request: RemoveOrUnassignedLocationsRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.RemoveOrUnassignedLocations}`,
        request
      )
      .pipe(
        map((response: BaseResponse<boolean>) => response.successful),
        catchError((err) => {
          throw err;
        })
      );
  }

  deleteUnassignedLocations(
    request: RemoveOrUnassignedLocationsRequest[]
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.DeleteUnassignedLocations}`,
        request
      )
      .pipe(
        map((response: BaseResponse<boolean>) => response.successful),
        catchError((err) => {
          throw err;
        })
      );
  }

  getLookupLocationsByJobId(
    request: GetLocationLookupRequest
  ): Observable<LocationLookup[]> {
    return this.http
      .post<BaseResponse<LocationLookupWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetLookupLocations}`,
        request
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<LocationLookupWebEntity[]>) =>
            response.result!
        )
      )
      .pipe(map(this.locationLookupWebRepositoryMapper.mapFrom))
      .pipe(
        reduce(
          (items: LocationLookup[], item: LocationLookup) => [...items, item],
          []
        )
      );
  }
}
